import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, X, Star, Camera, ImagePlus, Video, Loader2, Copy, Archive, Package, Sparkles, Boxes, ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import { api, money, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const MANUAL_BADGES = [
  ["staff_pick", "Staff Pick"], ["hot_item", "Hot Item"], ["rare_find", "Rare Find"],
  ["just_listed", "Just Listed"], ["top_seller", "Top Seller"], ["customer_favorite", "Customer Favorite"],
];

const EMPTY = {
  name: "", category: "", description: "", price: "", price_label: "Message for price",
  images: [], video: "", weight_oz: 16, stock: 1, track_inventory: true, free_shipping: false,
  allow_shipping: true, allow_pickup: true,
  clearance: false, custom_badges: [],
  condition: "", condition_details: "", brand: "", authenticity_status: "not_applicable", sku: "", storage_location: "",
  gtin: "", mpn: "", color: "", size: "", gender: "", age_group: "", google_product_category: "",
  featured: false, active: true, showcase: false, draft: false, archived: false, sold_out: false,
  is_dropship: false, supplier_name: "", supplier_url: "", supplier_cost: "",
};

const AUTH_LABELS = {
  not_applicable: "Not applicable",
  awaiting_review: "Awaiting review (hidden)",
  unverified: "Unverified (hidden)",
  documentation_reviewed: "Documentation reviewed",
  third_party_authenticated: "Third-party authenticated",
  rejected: "Rejected / do not publish (hidden)",
};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploads, setUploads] = useState([]);
  const [meta, setMeta] = useState({ condition_options: [], authenticity_statuses: [] });
  const [errors, setErrors] = useState({});
  const cameraRef = useRef(null);
  const libraryRef = useRef(null);
  const videoRef = useRef(null);
  const nameRef = useRef(null);
  const conditionRef = useRef(null);
  const conditionDetailsRef = useRef(null);
  const [chooser, setChooser] = useState(false);
  const [sourceTab, setSourceTab] = useState("all");
  const [affiliateRecs, setAffiliateRecs] = useState([]);
  const navigate = useNavigate();

  const load = () => api.get("/products", { params: { include_inactive: true } }).then((r) => setProducts(r.data));

  useEffect(() => {
    load();
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/admin/affiliates").then((r) => setAffiliateRecs(r.data)).catch(() => {});
    api.get("/config").then((r) => setMeta({
      condition_options: r.data.condition_options || [],
      authenticity_statuses: r.data.authenticity_statuses || [],
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setChooser(false);
    setEditing(null);
    setErrors({});
    setUploads([]);
    setForm({ ...EMPTY, category: categories[0] || "" });
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setErrors({});
    setUploads([]);
    setForm({
      name: p.name, category: p.category, description: p.description || "",
      price: p.price ?? "", price_label: p.price_label || "Message for price",
      images: p.images || [], video: p.video_url || "", weight_oz: p.weight_oz ?? 16, stock: p.stock || 0,
      track_inventory: p.track_inventory || false, free_shipping: p.free_shipping || false,
      allow_shipping: p.allow_shipping !== false, allow_pickup: p.allow_pickup !== false,
      clearance: p.clearance || false, custom_badges: p.custom_badges || [],
      condition: p.condition || "", condition_details: p.condition_details || "",
      brand: p.brand || "", authenticity_status: p.authenticity_status || "not_applicable", sku: p.sku || "", storage_location: p.storage_location || "",
      gtin: p.gtin || "", mpn: p.mpn || "", color: p.color || "", size: p.size || "", gender: p.gender || "", age_group: p.age_group || "", google_product_category: p.google_product_category || "",
      featured: p.featured, active: p.active, showcase: p.showcase, draft: p.draft || false, archived: p.archived || false, sold_out: p.sold_out || false,
      is_dropship: p.is_dropship || false, supplier_name: p.supplier_name || "",
      supplier_url: p.supplier_url || "", supplier_cost: p.supplier_cost ?? "",
    });
    setOpen(true);
  };

  const uploadOne = async (file) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setUploads((u) => [...u, { id, name: file.name || "photo", status: "uploading", file }]);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (r.data.type === "video") {
        setForm((f) => ({ ...f, video: r.data.url }));
      } else {
        setForm((f) => ({ ...f, images: [...f.images, r.data.url] }));
      }
      setUploads((u) => u.filter((x) => x.id !== id));
    } catch (err) {
      toast.error(`${file.name || "Photo"}: ${formatApiError(err.response?.data?.detail) || "upload failed"}`);
      setUploads((u) => u.map((x) => (x.id === id ? { ...x, status: "error" } : x)));
    }
  };

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    // Sequential keeps memory low for large iPhone photos; form data is preserved throughout.
    for (const file of files) await uploadOne(file);
    setUploading(false);
  };

  const retryUpload = (item) => {
    setUploads((u) => u.filter((x) => x.id !== item.id));
    uploadOne(item.file);
  };

  const dismissUpload = (item) => setUploads((u) => u.filter((x) => x.id !== item.id));

  const removeImage = (url) => setForm((f) => ({ ...f, images: f.images.filter((u) => u !== url) }));

  const moveImage = (idx, dir) => setForm((f) => {
    const imgs = [...f.images];
    const ni = idx + dir;
    if (ni < 0 || ni >= imgs.length) return f;
    [imgs[idx], imgs[ni]] = [imgs[ni], imgs[idx]];
    return { ...f, images: imgs };
  });

  const makePrimary = (idx) => setForm((f) => {
    if (idx === 0) return f;
    const imgs = [...f.images];
    const [x] = imgs.splice(idx, 1);
    imgs.unshift(x);
    return { ...f, images: imgs };
  });

  const save = async (e) => {
    e.preventDefault();
    // Never save while photos are still uploading — prevents products saved with images=[].
    if (uploading || uploads.some((u) => u.status === "uploading")) {
      toast.error("Photos are still uploading — please wait until they finish.");
      return;
    }
    // Don't silently drop failed photos — make the user retry or remove them first.
    if (uploads.some((u) => u.status === "error")) {
      toast.error("Some photos failed to upload. Retry or remove them before saving.");
      return;
    }
    const priceNum = form.price === "" || form.price === null ? null : Number(form.price);
    const isResale = priceNum != null && !form.showcase && !form.is_dropship;
    const newErrors = {};
    if (!form.name?.trim()) newErrors.name = "Product name is required.";
    if (isResale && !form.free_shipping && !(Number(form.weight_oz) > 0)) newErrors.weight = "A shipping weight greater than 0 oz is required (or mark the item Free shipping).";
    if (isResale && !form.condition?.trim()) newErrors.condition = "Condition is required for priced resale items — choose one from the list.";
    if (isResale && (form.condition_details || "").trim().length < 5) newErrors.condition_details = "Condition details must be at least 5 characters (describe wear, flaws, packaging, etc.).";
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      const missing = Object.values(newErrors).join(" ");
      toast.error(missing);
      const target = newErrors.name ? nameRef.current
        : newErrors.condition ? conditionRef.current
        : conditionDetailsRef.current;
      if (target) target.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setErrors({});
    setSaving(true);
    const payload = {
      name: form.name,
      category: form.category,
      description: form.description,
      price: priceNum,
      price_label: priceNum != null ? money(priceNum) : (form.price_label || "Message for price"),
      images: form.images || [],
      video_url: form.video || "",
      weight_oz: Number(form.weight_oz) || 16,
      stock: Number(form.stock) || 0,
      track_inventory: form.track_inventory,
      free_shipping: form.free_shipping,
      allow_shipping: form.allow_shipping,
      allow_pickup: form.allow_pickup,
      clearance: form.clearance,
      custom_badges: form.custom_badges,
      condition: form.condition || "",
      condition_details: form.condition_details || "",
      brand: form.brand || "",
      authenticity_status: form.authenticity_status || "not_applicable",
      sku: form.sku || "",
      storage_location: form.storage_location || "",
      gtin: form.gtin || "",
      mpn: form.mpn || "",
      color: form.color || "",
      size: form.size || "",
      gender: form.gender || "",
      age_group: form.age_group || "",
      google_product_category: form.google_product_category || "",
      featured: form.featured,
      active: form.active,
      draft: form.draft,
      archived: form.archived,
      sold_out: form.sold_out,
      showcase: priceNum == null ? true : form.showcase,
      is_dropship: form.is_dropship,
      supplier_name: form.supplier_name,
      supplier_url: form.supplier_url,
      supplier_cost: form.supplier_cost === "" || form.supplier_cost === null ? null : Number(form.supplier_cost),
    };
    try {
      if (editing) await api.put(`/admin/products/${editing.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(editing ? "Product updated" : "Product added — your photos and details are saved.");
      setOpen(false);
      load();
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      // Accurate, field-specific server errors; the form & uploaded photos are kept (modal stays open).
      let msg = formatApiError(detail);
      if (status === 422) msg = `The server rejected a field: ${msg}. Your photos and entries are kept — please correct and try again.`;
      else if (status === 401) msg = "Your session expired. Please sign in again — your work has been kept.";
      else if (!err.response) msg = "Network problem — the product was not saved. Your photos and entries are kept; tap Save to retry.";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This permanently removes the listing.`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Product deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const duplicate = async (p) => {
    try {
      await api.post(`/admin/products/${p.id}/duplicate`);
      toast.success("Duplicated as a draft — edit and publish when ready");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const quickPatch = async (p, patch, msg) => {
    try {
      await api.put(`/admin/products/${p.id}`, patch);
      toast.success(msg);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-products">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-4xl text-navy">Products</h1>
          <p className="text-ink/60 mt-1">Add inventory as you go. Leave price empty for "Message for price" items.</p>
        </div>
        <button data-testid="add-product-btn" onClick={() => setChooser(true)} className="inline-flex items-center gap-2 bg-brick text-white font-bold uppercase rounded-md px-5 py-3 hover:bg-navy transition-colors">
          <Plus className="w-4 h-4" /> Add Product
        </button>
      </div>

      {/* Product-type chooser */}
      {chooser && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setChooser(false)}>
          <div data-testid="product-type-chooser" onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg w-full max-w-md p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl text-navy">What are you adding?</h2>
              <button onClick={() => setChooser(false)} className="text-navy/50 hover:text-brick"><X className="w-5 h-5" /></button>
            </div>
            <div className="mt-5 space-y-3">
              <button data-testid="choose-personal" onClick={openCreate} className="w-full flex items-start gap-3 text-left rounded-lg border border-navy/20 p-4 hover:border-brick hover:bg-cream/40 transition">
                <Package className="w-6 h-6 text-brick shrink-0" />
                <span><span className="block font-bold text-navy">Personal Inventory</span><span className="text-sm text-ink/65">Items you own, list and ship. Uses JohnnyMart checkout.</span></span>
              </button>
              <button data-testid="choose-amazon" onClick={() => navigate("/admin/affiliates")} className="w-full flex items-start gap-3 text-left rounded-lg border border-navy/20 p-4 hover:border-brick hover:bg-cream/40 transition">
                <Sparkles className="w-6 h-6 text-[#ff9900] shrink-0" />
                <span><span className="block font-bold text-navy">Amazon Affiliate Product</span><span className="text-sm text-ink/65">Paste an Amazon link — no inventory or shipping. Opens in Gorilla Gateway.</span></span>
              </button>
              <button data-testid="choose-dropship" onClick={() => navigate("/admin/dropshipping")} className="w-full flex items-start gap-3 text-left rounded-lg border border-navy/20 p-4 hover:border-brick hover:bg-cream/40 transition">
                <Boxes className="w-6 h-6 text-navy shrink-0" />
                <span><span className="block font-bold text-navy">Dropshipping / Other Partners</span><span className="text-sm text-ink/65">Supplier-shipped products. Opens the dropshipping manager.</span></span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Source tabs — classify inventory by where it comes from */}
      {(() => {
        const jmCount = products.filter((p) => (p.source_type || "johnny_mart") === "johnny_mart").length;
        const gwCount = products.filter((p) => p.source_type === "gateway" || p.is_dropship).length;
        const amazonCount = affiliateRecs.filter((r) => (r.source || "amazon") === "amazon").length;
        const partnerCount = affiliateRecs.length;
        const TABS = [
          { k: "all", label: "All Inventory", count: products.length },
          { k: "johnny_mart", label: "Johnny Mart", count: jmCount },
          { k: "gateway", label: "Gateway", count: gwCount },
          { k: "partner", label: "Other Partners", count: partnerCount },
        ];
        return (
          <div data-testid="source-tabs" className="mt-6 flex gap-2 flex-wrap">
            {TABS.map((t) => (
              <button key={t.k} data-testid={`source-tab-${t.k}`} onClick={() => setSourceTab(t.k)}
                className={`rounded-full px-4 py-2 text-sm font-bold transition-colors ${sourceTab === t.k ? "bg-brick text-white" : "bg-white border border-navy/20 text-navy hover:border-brick"}`}>
                {t.label} <span className="opacity-70">({t.count})</span>
                {t.k === "partner" && amazonCount > 0 && <span className="ml-1 text-[10px] bg-[#ff9900] text-navy rounded px-1">{amazonCount} Amazon</span>}
              </button>
            ))}
          </div>
        );
      })()}

      {/* Other Partners (affiliate) panel */}
      {sourceTab === "partner" ? (
        <div data-testid="partner-panel" className="mt-4 bg-white rounded-lg border border-navy/15 p-5">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-ink/70 text-sm">Amazon &amp; other affiliate recommendations. These are <b>not</b> JohnnyMart inventory — no cart, checkout, stock or shipping.</p>
            <button onClick={() => navigate("/admin/affiliates")} className="text-sm font-bold text-brick hover:text-navy">Manage in Gorilla Gateway →</button>
          </div>
          <div className="mt-4 divide-y divide-navy/10">
            {affiliateRecs.length === 0 && <p className="text-navy/50 py-4">No affiliate products yet.</p>}
            {affiliateRecs.map((r) => (
              <div key={r.id} data-testid={`partner-row-${r.id}`} className="flex items-center justify-between gap-3 py-3">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-[11px] font-bold rounded px-2 py-1 bg-[#ff9900] text-navy shrink-0">{(r.source || "amazon") === "amazon" ? "Amazon" : "Partner"}</span>
                  <span className="font-semibold text-navy truncate">{r.title}</span>
                  <span className={`text-xs font-bold rounded-full px-2 py-1 shrink-0 ${r.status === "published" ? "bg-green-100 text-green-700" : "bg-purple-100 text-purple-700"}`}>{r.status}</span>
                </div>
                <button onClick={() => navigate("/admin/affiliates")} className="p-2 text-navy hover:text-brick shrink-0"><Pencil className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      ) : (
      <div className="mt-4 bg-white rounded-lg border border-navy/15 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-navy text-cream text-left">
            <tr>
              <th className="px-4 py-3">Product</th>
              <th className="px-4 py-3 hidden sm:table-cell">Source</th>
              <th className="px-4 py-3 hidden sm:table-cell">Category</th>
              <th className="px-4 py-3">Price</th>
              <th className="px-4 py-3 hidden md:table-cell">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-navy/10">
            {products.filter((p) => {
              const st = p.source_type || (p.is_dropship ? "gateway" : "johnny_mart");
              return sourceTab === "all" || st === sourceTab;
            }).map((p) => {
              const st = p.source_type || (p.is_dropship ? "gateway" : "johnny_mart");
              const badge = st === "gateway" ? { t: "Gateway", c: "bg-indigo-100 text-indigo-700" } : { t: "Johnny Mart", c: "bg-teal-100 text-teal-700" };
              return (
              <tr key={p.id} data-testid={`admin-product-row-${p.slug}`} className="hover:bg-cream/50">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded bg-cream overflow-hidden shrink-0">
                      {p.images?.[0] && <img src={p.images[0]} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <span className="font-semibold text-navy flex items-center gap-1">
                      {p.featured && <Star className="w-3.5 h-3.5 text-gold fill-gold" />}
                      {p.name}
                    </span>
                    {p.inventory_id && <span data-testid={`inv-id-${p.slug}`} className="text-xs font-mono text-ink/50 shrink-0">{p.inventory_id}</span>}
                  </div>
                </td>
                <td className="px-4 py-3 hidden sm:table-cell">
                  <span data-testid={`source-badge-${p.slug}`} className={`text-xs font-bold rounded-full px-2 py-1 ${badge.c}`}>{badge.t}</span>
                </td>
                <td className="px-4 py-3 hidden sm:table-cell text-ink/70">{p.category}</td>
                <td className="px-4 py-3 font-semibold text-brick">{p.price != null ? money(p.price) : p.price_label}</td>
                <td className="px-4 py-3 hidden md:table-cell">
                  <span className={`text-xs font-bold rounded-full px-2 py-1 ${p.archived ? "bg-gray-300 text-gray-700" : p.draft ? "bg-purple-100 text-purple-700" : p.active ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-600"}`}>
                    {p.archived ? "Archived" : p.draft ? "Draft" : p.active ? "Active" : "Hidden"}
                  </span>
                  {["awaiting_review", "unverified", "rejected"].includes(p.authenticity_status) && (
                    <span data-testid={`review-flag-${p.slug}`} className="ml-1 text-xs font-bold rounded-full px-2 py-1 bg-amber-100 text-amber-700">
                      Review{p.flagged_brand ? `: ${p.flagged_brand}` : ""}
                    </span>
                  )}
                  {p.sold_out && (
                    <span className="ml-1 text-xs font-bold rounded-full px-2 py-1 bg-red-100 text-red-700">Sold Out</span>
                  )}
                  {p.needs_variant_review && (
                    <span data-testid={`needs-variant-${p.slug}`} className="ml-1 text-xs font-bold rounded-full px-2 py-1 bg-orange-100 text-orange-700">Needs Variant Review</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <button data-testid={`edit-product-${p.slug}`} onClick={() => openEdit(p)} title="Edit" className="p-2 text-navy hover:text-brick"><Pencil className="w-4 h-4" /></button>
                    <button data-testid={`duplicate-product-${p.slug}`} onClick={() => duplicate(p)} title="Duplicate" className="p-2 text-navy hover:text-brick"><Copy className="w-4 h-4" /></button>
                    <button data-testid={`archive-product-${p.slug}`} onClick={() => quickPatch(p, { archived: !p.archived }, p.archived ? "Restored from archive" : "Archived")} title={p.archived ? "Restore" : "Archive"} className="p-2 text-navy hover:text-brick"><Archive className="w-4 h-4" /></button>
                    <button data-testid={`delete-product-${p.slug}`} onClick={() => remove(p)} title="Delete" className="p-2 text-navy hover:text-brick"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setOpen(false)}>
          <form
            data-testid="product-form"
            onClick={(e) => e.stopPropagation()}
            onSubmit={save}
            className="bg-white rounded-lg w-full max-w-lg p-6 my-8"
          >
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl text-navy">{editing ? "Edit Product" : "Add Product"}</h2>
              <button type="button" onClick={() => setOpen(false)} className="text-navy/50 hover:text-brick"><X className="w-5 h-5" /></button>
            </div>
            {editing?.inventory_id && (
              <p data-testid="pf-inventory-id" className="mt-2 inline-flex items-center gap-2 rounded-full bg-navy/5 border border-navy/15 px-3 py-1 text-sm font-bold text-navy">
                Inventory ID: <span className="font-mono text-brick">{editing.inventory_id}</span>
              </p>
            )}
            {!editing && (
              <p data-testid="pf-inventory-id-note" className="mt-2 text-xs text-ink/50">An inventory ID (JM-####) will be assigned automatically when you save.</p>
            )}

            <label className="block text-sm font-bold text-navy uppercase mt-4">Name</label>
            <input ref={nameRef} data-testid="pf-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={`mt-1 w-full rounded-md border px-3 py-2.5 outline-none focus:border-brick ${errors.name ? "border-red-500" : "border-navy/20"}`} />
            {errors.name && <p data-testid="pf-name-error" role="alert" className="mt-1 text-sm font-semibold text-red-600">{errors.name}</p>}

            <label className="block text-sm font-bold text-navy uppercase mt-4">Category</label>
            <select data-testid="pf-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 w-full rounded-md border border-navy/20 px-3 py-2.5 outline-none focus:border-brick bg-white">
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>

            <label className="block text-sm font-bold text-navy uppercase mt-4">Description</label>
            <textarea data-testid="pf-description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 w-full rounded-md border border-navy/20 px-3 py-2.5 outline-none focus:border-brick resize-none" />

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-navy uppercase mt-4">Price (USD)</label>
                <input data-testid="pf-price" type="number" step="0.01" min="0" placeholder="Leave empty for inquiry" value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} className="mt-1 w-full rounded-md border border-navy/20 px-3 py-2.5 outline-none focus:border-brick" />
              </div>
              <div>
                <label className="block text-sm font-bold text-navy uppercase mt-4">Stock</label>
                <input data-testid="pf-stock" type="number" min="0" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="mt-1 w-full rounded-md border border-navy/20 px-3 py-2.5 outline-none focus:border-brick" />
              </div>
            </div>

            <label className="block text-sm font-bold text-navy uppercase mt-4">Shipping weight (oz)</label>
            <input data-testid="pf-weight" type="number" step="0.1" min="0" placeholder="e.g. 16 = 1 lb" value={form.weight_oz} onChange={(e) => setForm({ ...form, weight_oz: e.target.value })} className={`mt-1 w-full rounded-md border px-3 py-2.5 outline-none focus:border-brick ${errors.weight ? "border-red-500" : "border-navy/20"}`} />
            {errors.weight && <p data-testid="pf-weight-error" role="alert" className="mt-1 text-sm font-semibold text-red-600">{errors.weight}</p>}
            <p className="text-xs text-ink/50 mt-1">Used to calculate USPS shipping at checkout. 16 oz = 1 lb. Include packaging.</p>

            <label className="flex items-center gap-2 text-sm font-semibold text-navy mt-3">
              <input data-testid="pf-free-shipping" type="checkbox" checked={form.free_shipping} onChange={(e) => setForm({ ...form, free_shipping: e.target.checked })} />
              Free shipping (intentionally ship this item free — otherwise shipping is always charged by weight)
            </label>

            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <label data-testid="pf-allow-shipping" className={`flex items-center gap-2 rounded-md border px-3 py-2.5 text-sm font-semibold cursor-pointer ${form.allow_shipping ? "border-green-300 bg-green-50 text-green-800" : "border-navy/15 text-navy/60"}`}>
                <input type="checkbox" checked={form.allow_shipping} onChange={(e) => setForm({ ...form, allow_shipping: e.target.checked })} />
                Shipping available
              </label>
              <label data-testid="pf-allow-pickup" className={`flex items-center gap-2 rounded-md border px-3 py-2.5 text-sm font-semibold cursor-pointer ${form.allow_pickup ? "border-green-300 bg-green-50 text-green-800" : "border-navy/15 text-navy/60"}`}>
                <input type="checkbox" checked={form.allow_pickup} onChange={(e) => setForm({ ...form, allow_pickup: e.target.checked })} />
                Local pickup available (paid online first)
              </label>
            </div>

            <label className="flex items-center gap-2 text-sm font-semibold text-navy mt-4">
              <input data-testid="pf-track-inventory" type="checkbox" checked={form.track_inventory} onChange={(e) => setForm({ ...form, track_inventory: e.target.checked })} />
              Track inventory (one-of-a-kind — auto "Sold Out" at 0, prevents double-selling)
            </label>

            <label ref={conditionRef} className="block text-sm font-bold text-navy uppercase mt-4">Condition <span className="text-brick">*</span> <span className="text-ink/40 normal-case font-normal">(required for resale items)</span></label>
            <select data-testid="pf-condition" value={form.condition} onChange={(e) => setForm((f) => ({ ...f, condition: e.target.value }))} className={`mt-1 w-full rounded-md border px-3 py-2.5 outline-none focus:border-brick bg-white ${errors.condition ? "border-red-500" : "border-navy/20"}`}>
              <option value="">— Select condition —</option>
              {meta.condition_options.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            {errors.condition && <p data-testid="pf-condition-error" role="alert" className="mt-1 text-sm font-semibold text-red-600">{errors.condition}</p>}

            <label ref={conditionDetailsRef} className="block text-sm font-bold text-navy uppercase mt-4">Condition details <span className="text-brick">*</span></label>
            <textarea data-testid="pf-condition-details" rows={2} placeholder="Describe wear, stains, damage, missing parts, repairs, odors, packaging/tag condition…" value={form.condition_details} onChange={(e) => setForm((f) => ({ ...f, condition_details: e.target.value }))} className={`mt-1 w-full rounded-md border px-3 py-2.5 outline-none focus:border-brick resize-none ${errors.condition_details ? "border-red-500" : "border-navy/20"}`} />
            {errors.condition_details && <p data-testid="pf-condition-details-error" role="alert" className="mt-1 text-sm font-semibold text-red-600">{errors.condition_details}</p>}

            <label className="block text-sm font-bold text-navy uppercase mt-4">Brand (only if accurately identified)</label>
            <input data-testid="pf-brand" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="mt-1 w-full rounded-md border border-navy/20 px-3 py-2.5 outline-none focus:border-brick" />

            <label className="block text-sm font-bold text-navy uppercase mt-4">Size <span className="text-ink/40 normal-case font-normal">(when appropriate)</span></label>
            <input data-testid="pf-size-main" value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} placeholder="e.g. M, XL, 8, 10.5, One Size" className="mt-1 w-full rounded-md border border-navy/20 px-3 py-2.5 outline-none focus:border-brick" />

            <label className="flex items-center gap-2 text-sm font-semibold text-navy mt-4">
              <input data-testid="pf-clearance" type="checkbox" checked={form.clearance} onChange={(e) => setForm({ ...form, clearance: e.target.checked })} />
              Clearance item (shows under the Clearance shopper filter)
            </label>

            <label className="block text-sm font-bold text-navy uppercase mt-4">Customer badges <span className="text-ink/40 normal-case font-normal">(optional — "JohnnyMart Verified" is automatic once QC is 8/8)</span></label>
            <div className="mt-1 grid grid-cols-2 gap-2">
              {MANUAL_BADGES.map(([key, label]) => (
                <label key={key} data-testid={`pf-badge-${key}`} className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-semibold cursor-pointer ${form.custom_badges.includes(key) ? "border-teal-600 bg-teal-50 text-teal-800" : "border-navy/15 text-navy"}`}>
                  <input type="checkbox" checked={form.custom_badges.includes(key)}
                    onChange={(e) => setForm({ ...form, custom_badges: e.target.checked ? [...form.custom_badges, key] : form.custom_badges.filter((b) => b !== key) })} />
                  {label}
                </label>
              ))}
            </div>

            <details className="mt-5 rounded-lg border border-navy/15 bg-cream/40" data-testid="pf-advanced">
              <summary className="cursor-pointer select-none px-4 py-3 text-sm font-bold text-navy uppercase">Advanced <span className="text-ink/40 normal-case font-normal">(rarely needed — SKU, Google Shopping, authenticity)</span></summary>
              <div className="px-4 pb-4">
              <label className="block text-sm font-bold text-navy uppercase mt-2">SKU <span className="text-ink/40 normal-case font-normal">(optional — inventory IDs are automatic)</span></label>
              <input data-testid="pf-sku" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className="mt-1 w-full rounded-md border border-navy/20 px-3 py-2.5 outline-none focus:border-brick" />

              <label className="block text-sm font-bold text-navy uppercase mt-4">Storage location <span className="text-ink/40 normal-case font-normal">(optional, admin-only)</span></label>
              <input data-testid="pf-storage-location" value={form.storage_location} onChange={(e) => setForm({ ...form, storage_location: e.target.value })} placeholder='e.g. "Garage Shelf B3", "Closet Bin 2"' className="mt-1 w-full rounded-md border border-navy/20 px-3 py-2.5 outline-none focus:border-brick" />

              <div className="mt-4">
              <p className="text-sm font-bold text-navy uppercase">Google Shopping details <span className="text-ink/40 normal-case font-normal">(optional — clears Merchant Center warnings)</span></p>
              <p className="text-xs text-ink/50 mt-0.5">Leave blank if it doesn't apply. Color, size, gender &amp; age group are recommended for apparel and shoes. Only enter a GTIN/MPN if it's a real manufacturer identifier.</p>

              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-navy uppercase">Color</label>
                  <input data-testid="pf-color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} placeholder="e.g. Black, Navy Blue, Red/White" className="mt-1 w-full rounded-md border border-navy/20 px-3 py-2 outline-none focus:border-brick" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-navy uppercase">Size</label>
                  <input data-testid="pf-size" value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} placeholder="e.g. M, XL, 8, 10.5, One Size, OSFA" className="mt-1 w-full rounded-md border border-navy/20 px-3 py-2 outline-none focus:border-brick" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-navy uppercase">Gender</label>
                  <select data-testid="pf-gender" value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} className="mt-1 w-full rounded-md border border-navy/20 px-3 py-2 outline-none focus:border-brick bg-white">
                    <option value="">— Not set —</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="unisex">Unisex</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-navy uppercase">Age group</label>
                  <select data-testid="pf-age-group" value={form.age_group} onChange={(e) => setForm({ ...form, age_group: e.target.value })} className="mt-1 w-full rounded-md border border-navy/20 px-3 py-2 outline-none focus:border-brick bg-white">
                    <option value="">— Not set —</option>
                    <option value="newborn">Newborn</option>
                    <option value="infant">Infant</option>
                    <option value="toddler">Toddler</option>
                    <option value="kids">Kids</option>
                    <option value="adult">Adult</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-navy uppercase">GTIN / UPC / EAN / ISBN</label>
                  <input data-testid="pf-gtin" value={form.gtin} onChange={(e) => setForm({ ...form, gtin: e.target.value })} placeholder="Real barcode number only" className="mt-1 w-full rounded-md border border-navy/20 px-3 py-2 outline-none focus:border-brick" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-navy uppercase">MPN</label>
                  <input data-testid="pf-mpn" value={form.mpn} onChange={(e) => setForm({ ...form, mpn: e.target.value })} placeholder="Manufacturer part number" className="mt-1 w-full rounded-md border border-navy/20 px-3 py-2 outline-none focus:border-brick" />
                </div>
              </div>
              <div className="mt-3">
                <label className="block text-xs font-bold text-navy uppercase">Google product category override <span className="text-ink/40 normal-case font-normal">(optional)</span></label>
                <input data-testid="pf-google-category" value={form.google_product_category} onChange={(e) => setForm({ ...form, google_product_category: e.target.value })} placeholder="e.g. Apparel & Accessories > Clothing > Shirts & Tops" className="mt-1 w-full rounded-md border border-navy/20 px-3 py-2 outline-none focus:border-brick" />
              </div>
              </div>

              <label className="block text-sm font-bold text-navy uppercase mt-4">Authenticity status</label>
              <select data-testid="pf-authenticity" value={form.authenticity_status} onChange={(e) => setForm({ ...form, authenticity_status: e.target.value })} className="mt-1 w-full rounded-md border border-navy/20 px-3 py-2.5 outline-none focus:border-brick bg-white">
                {meta.authenticity_statuses.map((s) => <option key={s} value={s}>{AUTH_LABELS[s] || s}</option>)}
              </select>
              <p className="text-xs text-ink/50 mt-1">Designer-branded items are auto-set to "Awaiting review" and hidden from the public store until you set a published status here.</p>
              </div>
            </details>

            <label className="block text-sm font-bold text-navy uppercase mt-4">Photos &amp; Video</label>
            <p className="text-xs text-ink/50 mt-0.5">Add photos from your iPhone (HEIC supported). The first photo is the main image — use the buttons to reorder or change it.</p>

            {form.images?.length > 0 && (
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="pf-image-grid">
                {form.images.map((url, idx) => (
                  <div key={url} data-testid={`pf-image-${idx}`} className="rounded-lg border border-navy/15 overflow-hidden bg-white">
                    <div className="relative aspect-square">
                      <img src={url} alt="" className="w-full h-full object-cover" />
                      {idx === 0 && (
                        <span data-testid="pf-primary-badge" className="absolute top-1 left-1 inline-flex items-center gap-1 bg-gold text-navy text-[10px] font-bold rounded px-1.5 py-0.5">
                          <Star className="w-3 h-3 fill-navy" /> Main
                        </span>
                      )}
                      <button type="button" data-testid={`pf-remove-image-${idx}`} onClick={() => removeImage(url)}
                        className="absolute top-1 right-1 bg-black/65 text-white rounded-full p-1.5">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="flex items-stretch divide-x divide-navy/10 border-t border-navy/10">
                      <button type="button" data-testid={`pf-move-left-${idx}`} onClick={() => moveImage(idx, -1)} disabled={idx === 0}
                        className="flex-1 py-2.5 flex items-center justify-center text-navy disabled:opacity-30 active:bg-cream" aria-label="Move up">
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <button type="button" data-testid={`pf-make-primary-${idx}`} onClick={() => makePrimary(idx)} disabled={idx === 0}
                        className="flex-1 py-2.5 flex items-center justify-center text-navy disabled:opacity-30 active:bg-cream" aria-label="Make primary">
                        <Star className={`w-4 h-4 ${idx === 0 ? "fill-gold text-gold" : ""}`} />
                      </button>
                      <button type="button" data-testid={`pf-move-right-${idx}`} onClick={() => moveImage(idx, 1)} disabled={idx === form.images.length - 1}
                        className="flex-1 py-2.5 flex items-center justify-center text-navy disabled:opacity-30 active:bg-cream" aria-label="Move down">
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {form.video && (
              <div className="mt-3 relative w-28 h-28 rounded-md overflow-hidden border border-navy/15 bg-black">
                <video src={form.video} className="w-full h-full object-cover" muted />
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none"><Video className="w-6 h-6 text-white/80" /></div>
                <button type="button" data-testid="pf-remove-video" onClick={() => setForm({ ...form, video: "" })}
                  className="absolute top-1 right-1 bg-black/65 text-white rounded-full p-1.5"><X className="w-4 h-4" /></button>
              </div>
            )}

            {/* In-progress / failed uploads with retry */}
            {uploads.length > 0 && (
              <div className="mt-3 space-y-2" data-testid="pf-upload-status">
                {uploads.map((u) => (
                  <div key={u.id} className={`flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm ${u.status === "error" ? "border-brick/40 bg-brick/5" : "border-navy/15 bg-cream/50"}`}>
                    <span className="truncate flex items-center gap-2 text-navy">
                      {u.status === "uploading" ? <Loader2 className="w-4 h-4 animate-spin text-brick" /> : <X className="w-4 h-4 text-brick" />}
                      <span className="truncate">{u.name}</span>
                    </span>
                    {u.status === "error" && (
                      <span className="shrink-0 flex items-center gap-3">
                        <button type="button" data-testid="pf-retry-upload" onClick={() => retryUpload(u)} className="inline-flex items-center gap-1 text-brick font-bold text-xs">
                          <RefreshCw className="w-3.5 h-3.5" /> Retry
                        </button>
                        <button type="button" data-testid="pf-remove-upload" onClick={() => dismissUpload(u)} className="inline-flex items-center gap-1 text-ink/60 font-bold text-xs">
                          <X className="w-3.5 h-3.5" /> Remove
                        </button>
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="mt-3 grid grid-cols-3 gap-2">
              <button type="button" data-testid="pf-take-photo" onClick={() => cameraRef.current?.click()}
                className="flex flex-col items-center gap-1 rounded-md border border-navy/20 py-3.5 text-navy hover:border-brick hover:text-brick transition-colors text-xs font-bold">
                <Camera className="w-5 h-5" /> Take Photo
              </button>
              <button type="button" data-testid="pf-choose-photos" onClick={() => libraryRef.current?.click()}
                className="flex flex-col items-center gap-1 rounded-md border border-navy/20 py-3.5 text-navy hover:border-brick hover:text-brick transition-colors text-xs font-bold">
                <ImagePlus className="w-5 h-5" /> Library
              </button>
              <button type="button" data-testid="pf-add-video" onClick={() => videoRef.current?.click()}
                className="flex flex-col items-center gap-1 rounded-md border border-navy/20 py-3.5 text-navy hover:border-brick hover:text-brick transition-colors text-xs font-bold">
                <Video className="w-5 h-5" /> Add Video
              </button>
            </div>
            {uploading && <p className="mt-2 text-sm text-brick flex items-center gap-2" data-testid="pf-uploading"><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</p>}

            <input ref={cameraRef} type="file" accept="image/*,.heic,.heif" capture="environment" className="hidden"
              onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }} />
            <input ref={libraryRef} type="file" accept="image/*,.heic,.heif,image/heic,image/heif" multiple className="hidden"
              data-testid="pf-library-input"
              onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }} />
            <input ref={videoRef} type="file" accept="video/*" className="hidden"
              onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }} />

            <div className="flex flex-wrap gap-5 mt-5">
              <label className="flex items-center gap-2 text-sm font-semibold text-navy">
                <input data-testid="pf-featured" type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} /> Featured
              </label>
              <label className="flex items-center gap-2 text-sm font-semibold text-navy">
                <input data-testid="pf-active" type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active
              </label>
              <label className="flex items-center gap-2 text-sm font-semibold text-navy">
                <input data-testid="pf-draft" type="checkbox" checked={form.draft} onChange={(e) => setForm({ ...form, draft: e.target.checked })} /> Draft (hidden)
              </label>
              <label className="flex items-center gap-2 text-sm font-semibold text-navy">
                <input data-testid="pf-sold-out" type="checkbox" checked={form.sold_out || false} onChange={(e) => setForm({ ...form, sold_out: e.target.checked })} /> Sold Out
              </label>
              <label className="flex items-center gap-2 text-sm font-semibold text-navy">
                <input data-testid="pf-archived" type="checkbox" checked={form.archived} onChange={(e) => setForm({ ...form, archived: e.target.checked })} /> Archived
              </label>
            </div>

            <div className="mt-5 rounded-lg border border-navy/15 bg-cream/50 p-4">
              <label className="flex items-center gap-2 text-sm font-bold text-navy">
                <input data-testid="pf-dropship" type="checkbox" checked={form.is_dropship} onChange={(e) => setForm({ ...form, is_dropship: e.target.checked })} />
                Drop-ship item (fulfilled by an outside supplier)
              </label>
              {form.is_dropship && (
                <div className="mt-3 grid gap-3">
                  <input data-testid="pf-supplier-name" placeholder="Supplier name" value={form.supplier_name} onChange={(e) => setForm({ ...form, supplier_name: e.target.value })} className="w-full rounded-md border border-navy/20 px-3 py-2.5 outline-none focus:border-brick" />
                  <input data-testid="pf-supplier-url" placeholder="Supplier product link (where you order it)" value={form.supplier_url} onChange={(e) => setForm({ ...form, supplier_url: e.target.value })} className="w-full rounded-md border border-navy/20 px-3 py-2.5 outline-none focus:border-brick" />
                  <input data-testid="pf-supplier-cost" type="number" step="0.01" min="0" placeholder="Your cost from supplier (for markup tracking)" value={form.supplier_cost} onChange={(e) => setForm({ ...form, supplier_cost: e.target.value })} className="w-full rounded-md border border-navy/20 px-3 py-2.5 outline-none focus:border-brick" />
                  <p className="text-xs text-ink/50">When a customer buys this, your order alert includes the supplier link so you can order it and have them ship directly to the buyer. Customers never see supplier info.</p>
                </div>
              )}
            </div>

            <button data-testid="save-product-btn" disabled={saving || uploading} className="mt-6 w-full bg-brick text-white font-bold uppercase rounded-md py-3 hover:bg-navy transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
              {(saving || uploading) && <Loader2 className="w-4 h-4 animate-spin" />}
              {uploading ? "Uploading photos…" : saving ? "Saving..." : editing ? "Save Changes" : "Add Product"}
            </button>
            {Object.keys(errors).length > 0 && (
              <p data-testid="pf-submit-error" className="mt-2 text-center text-sm font-semibold text-red-600">
                Couldn't save — please fill in the highlighted required fields above.
              </p>
            )}
          </form>
        </div>
      )}
    </div>
  );
}
